import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { Check } from 'lucide-react';
import bgImage from '../img/ID_BKG.jpeg';
import "./Profile.css";

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [mealDistribution, setMealDistribution] = useState([
    { id: 1, date: '2025-10-29', breakfast: true, lunch: true, hiTea: true, dinner: false },
    { id: 2, date: '2025-10-30', breakfast: true, lunch: false, hiTea: false, dinner: false },
    { id: 3, date: '2025-10-31', breakfast: false, lunch: false, hiTea: false, dinner: false },
    { id: 4, date: '2025-11-01', breakfast: false, lunch: false, hiTea: false, dinner: false },
    { id: 5, date: '2025-11-02', breakfast: false, lunch: false, hiTea: false, dinner: false }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrData, setQrData] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const idCardRef = useRef(null);

  // Get user photo URL
  const getUserPhotoUrl = (userData) => {
    if (!userData) return null;

    const photoFields = [
      'photoPath', 'PhotoPath', 'photo', 'Photo',
      'photoUrl', 'PhotoUrl', 'PhotoURL',
      'image', 'Image', 'imageUrl', 'ImageUrl', 'ImageURL',
      'profilePicture', 'ProfilePicture',
      'profilePhoto', 'ProfilePhoto',
      'avatar', 'Avatar', 'picture', 'Picture'
    ];

    for (const field of photoFields) {
      if (userData[field] && userData[field].toString().trim()) {
        const photoValue = userData[field].toString().trim();

        if (photoValue.startsWith('http://') || photoValue.startsWith('https://')) {
          return photoValue;
        }
        if (photoValue.startsWith('data:image')) {
          return photoValue;
        }
        if (photoValue.startsWith('/')) {
          return `https://api.asterickx.com${photoValue}`;
        }
        return `https://api.asterickx.com/uploads/${photoValue}`;
      }
    }
    return null;
  };

  const getUserPhone = (userData) => {
    if (!userData) return null;
    const phoneFields = ['phoneNumber', 'PhoneNumber', 'phone', 'Phone', 'mobile', 'Mobile', 'contactNumber', 'ContactNumber'];
    for (const field of phoneFields) {
      if (userData[field] && userData[field].toString().trim()) {
        return userData[field].toString().trim();
      }
    }
    return null;
  };

  const getUserState = (userData) => {
    if (!userData) return null;
    const stateFields = ['state', 'State', 'stateName', 'StateName', 'location', 'Location', 'region', 'Region'];
    for (const field of stateFields) {
      if (userData[field] && userData[field].toString().trim()) {
        return userData[field].toString().trim();
      }
    }
    return null;
  };

  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('JWT decode error:', error);
      return null;
    }
  };

  // FIXED: Check auth with retry and better token expiration handling
  const checkAuthWithRetry = async (retryCount = 0, maxRetries = 3) => {
    const storedUser = localStorage.getItem('user');
    let token = localStorage.getItem('token')
      || localStorage.getItem('Token')
      || (storedUser ? JSON.parse(storedUser).token : null);

    console.log(`=== AUTH CHECK (Attempt ${retryCount + 1}/${maxRetries + 1}) ===`);
    console.log('Token (lowercase):', localStorage.getItem('token') ? 'Found' : 'Not found');
    console.log('Token (uppercase):', localStorage.getItem('Token') ? 'Found' : 'Not found');
    console.log('Final token:', token ? 'EXISTS' : 'NULL');

    if (!token && retryCount < maxRetries) {
      console.log(`⏳ Token not found, retrying in 200ms... (${retryCount + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, 200));
      return checkAuthWithRetry(retryCount + 1, maxRetries);
    }

    console.log('==================\n');

    if (!token) {
      console.error('❌ No token found after all retries');
      navigate('/login');
      return null;
    }

    try {
      const decodedToken = decodeJWT(token);
      console.log('✓ Token decoded:', decodedToken);

      if (!decodedToken) {
        console.error('❌ Failed to decode token');
        navigate('/login');
        return null;
      }

      const userFromToken = {
        id: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
          || decodedToken.sub
          || decodedToken.userId,
        email: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress']
          || decodedToken.email,
        name: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
          || decodedToken.name,
        exp: decodedToken.exp
      };

      console.log('✓ User extracted from token:', userFromToken);

      // IMPORTANT: Check token expiration with buffer
      // if (userFromToken.exp) {
      //   const now = Math.floor(Date.now() / 1000); // Current time in seconds
      //   const expTime = userFromToken.exp;
      //   const timeUntilExpiry = expTime - now;

      //   console.log('=== TOKEN EXPIRATION CHECK ===');
      //   console.log('Current time (unix):', now);
      //   console.log('Token expires at (unix):', expTime);
      //   console.log('Time until expiry (seconds):', timeUntilExpiry);
      //   console.log('Time until expiry (minutes):', Math.floor(timeUntilExpiry / 60));
      //   console.log('==============================\n');

      //   if (timeUntilExpiry <= 0) {
      //     console.error('❌ Token is EXPIRED');
      //     console.error(`Token expired ${Math.abs(timeUntilExpiry)} seconds ago`);
          
      //     alert('Your session has expired. Please log in again.');
      //     localStorage.clear();
      //     navigate('/login');
      //     return null;
      //   } else if (timeUntilExpiry < 60) {
      //     console.warn(`⚠️ Token will expire in ${timeUntilExpiry} seconds`);
      //   } else {
      //     console.log(`✓ Token is valid for ${Math.floor(timeUntilExpiry / 60)} more minutes`);
      //   }
      // }

      return { userFromToken, token };

    } catch (parseError) {
      console.error('❌ Auth error:', parseError);
      localStorage.clear();
      navigate('/login');
      return null;
    }
  };

  useEffect(() => {
    const initializeProfile = async () => {
      const authResult = await checkAuthWithRetry();
      
      if (!authResult) {
        return;
      }

      const { userFromToken, token } = authResult;

      setUserData(userFromToken);

      if (!userFromToken.id) {
        setError('User ID not found');
        setLoading(false);
        return;
      }

      fetchUserDetails(userFromToken.id, token);
      generateQRData(userFromToken.id);
    };

    initializeProfile();
  }, [navigate]);

  // Download ID card
  const downloadIDCard = async () => {
    if (!idCardRef.current) {
      alert('ID card not found');
      return;
    }

    try {
      setDownloading(true);
      console.log('Downloading ID card as PDF...');

      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token')
        || localStorage.getItem('Token')
        || (storedUser ? JSON.parse(storedUser).token : null);

      const userId = userData?.id;

      let imageBase64 = null;
      if (token && userId) {
        try {
          const myHeaders = new Headers();
          myHeaders.append("Authorization", `Bearer ${token}`);

          const response = await fetch(
            `https://api.asterickx.com/api/Members/members/photo/${userId}`,
            { method: "GET", headers: myHeaders }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.base64) {
              imageBase64 = data.base64;
            }
          }
        } catch (error) {
          console.error('Error fetching base64:', error);
        }
      }

      const originalImages = [];
      if (imageBase64) {
        const images = idCardRef.current.querySelectorAll('img[alt="Profile"]');
        images.forEach(img => {
          originalImages.push({ element: img, src: img.src });
          const base64WithPrefix = imageBase64.startsWith('data:image')
            ? imageBase64
            : `data:image/jpeg;base64,${imageBase64}`;
          img.src = base64WithPrefix;
        });
      }

      const buttonsContainer = idCardRef.current.querySelector('[data-exclude-from-capture]');
      let originalDisplay = '';
      if (buttonsContainer) {
        originalDisplay = buttonsContainer.style.display;
        buttonsContainer.style.display = 'none';
      }

      const cardHeader = idCardRef.current.querySelector('.card-header');
      let originalMarginTop = '';
      if (cardHeader) {
        originalMarginTop = cardHeader.style.marginTop;
        cardHeader.style.marginTop = '1cm';
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(idCardRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedButtons = clonedDoc.querySelector('[data-exclude-from-capture]');
          if (clonedButtons) clonedButtons.remove();
        }
      });

      originalImages.forEach(({ element, src }) => {
        element.src = src;
      });

      if (buttonsContainer) {
        buttonsContainer.style.display = originalDisplay;
      }
      if (cardHeader) {
        cardHeader.style.marginTop = originalMarginTop;
      }

      const cardWidth = 100;
      const cardHeight = 130;
      const imgData = canvas.toDataURL('image/jpeg', 1.0);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [cardWidth, cardHeight],
        compress: false
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, cardWidth, cardHeight, undefined, 'SLOW');

      const displayUserData = userDetails || userData;
      const userName = (displayUserData?.firstName || displayUserData?.name || 'User').replace(/[^a-zA-Z0-9]/g, '_');
      pdf.save(`ID_Card_${userName}_${Date.now()}.pdf`);

    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download: ${error.message}`);
    } finally {
      setTimeout(() => setDownloading(false), 500);
    }
  };

  const generateQRData = async (userId) => {
    try {
      setQrLoading(true);
      const baseUrl = window.location.origin;
      const qrUrl = `${baseUrl}/userqr?id=${userId}`;
      setQrData(qrUrl);
      return qrUrl;
    } catch (error) {
      console.error('QR error:', error);
      const fallbackUrl = `${window.location.origin}/userqr?id=${userId}`;
      setQrData(fallbackUrl);
      return fallbackUrl;
    } finally {
      setQrLoading(false);
    }
  };

  // UPDATED fetchUserDetails function for Profile.jsx
// Replace the existing fetchUserDetails function with this

const fetchUserDetails = async (userId, token) => {
  try {
    setLoading(true);
    setError(null);

    console.log('=== FETCHING USER DETAILS ===');
    console.log('User ID:', userId);

    // Step 1: Fetch user details from Members API
    const apiUrl = `https://api.asterickx.com/api/Members/${userId}`;
    
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const userResponse = await fetch(apiUrl, {
      method: 'GET',
      headers: headers,
      mode: 'cors'
    });

    if (userResponse.ok) {
      const userDetailsData = await userResponse.json();
      console.log('✓ User details fetched successfully');
      setUserDetails(userDetailsData);
    } else {
      console.error('API failed:', userResponse.status);

      if (userResponse.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => {
          localStorage.clear();
          navigate('/login');
        }, 2000);
        return;
      }

      setError(`Failed: ${userResponse.status}`);
    }

    // Step 2: Fetch tournaments and find user's teams
    try {
      console.log('\n=== FETCHING TOURNAMENTS & TEAMS ===');
      
      // Fetch all tournaments
      const tournamentsResponse = await fetch(
        'https://api.asterickx.com/api/Tournaments',
        {
          headers: { 
            'Authorization': `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          }
        }
      );

      if (!tournamentsResponse.ok) {
        console.error('Failed to fetch tournaments:', tournamentsResponse.status);
        setTournaments([]);
        return;
      }

      const allTournaments = await tournamentsResponse.json();
      console.log('✓ Fetched tournaments:', allTournaments.length);
      console.log('All tournaments:', allTournaments);

      if (!Array.isArray(allTournaments) || allTournaments.length === 0) {
        console.log('No tournaments found');
        setTournaments([]);
        return;
      }

      // Step 3: For each tournament, fetch teams and check if user is a member
      const userTournaments = [];

      for (const tournament of allTournaments) {
        const tournamentId = tournament.id || tournament.tournamentId || tournament.TournamentId;
        
        if (!tournamentId) {
          console.warn('Tournament missing ID:', tournament);
          continue;
        }

        console.log(`\n--- Processing Tournament: ${tournament.name || tournamentId} ---`);

        try {
          // Fetch teams for this tournament
          const teamsResponse = await fetch(
            `https://api.asterickx.com/api/Teams/by-tournament/${tournamentId}`,
            {
              headers: { 
                'Authorization': `Bearer ${token}`, 
                'Content-Type': 'application/json' 
              }
            }
          );

          if (!teamsResponse.ok) {
            console.log(`No teams found for tournament ${tournamentId}`);
            continue;
          }

          const tournamentTeams = await teamsResponse.json();
          console.log(`✓ Found ${tournamentTeams.length} teams in tournament`);

          if (!Array.isArray(tournamentTeams) || tournamentTeams.length === 0) {
            continue;
          }

          // Step 4: Check each team to see if user is a member
          for (const team of tournamentTeams) {
            const teamId = team.id || team.teamId || team.TeamId;
            
            if (!teamId) {
              console.warn('Team missing ID:', team);
              continue;
            }

            console.log(`  Checking team: ${team.name || teamId}`);

            try {
              // Fetch team members
              const membersResponse = await fetch(
                `https://api.asterickx.com/api/Teams/${teamId}/members`,
                {
                  headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json' 
                  }
                }
              );

              if (!membersResponse.ok) {
                console.log(`  No members found for team ${teamId}`);
                continue;
              }

              const teamMembers = await membersResponse.json();
              console.log(`  ✓ Found ${teamMembers.length} members in team`);

              if (!Array.isArray(teamMembers)) {
                continue;
              }

              // Check if current user is in this team
              const isUserInTeam = teamMembers.some(member => {
                const memberId = String(member.id || member.memberId || member.userId || member.playerId || '');
                const currentUserId = String(userId);
                
                console.log(`    Comparing: member="${memberId}" vs user="${currentUserId}"`);
                
                return memberId === currentUserId;
              });

              if (isUserInTeam) {
                console.log(`  🎯 USER FOUND IN TEAM: ${team.name || teamId}`);
                
                // Add to user's tournaments
                userTournaments.push({
                  id: `${tournamentId}-${teamId}`,
                  tournamentId: tournamentId,
                  tournamentName: tournament.name || tournament.tournamentName || `Tournament ${tournamentId}`,
                  startDate: tournament.startDate || tournament.StartDate,
                  endDate: tournament.endDate || tournament.EndDate,
                  location: tournament.location || tournament.venue || tournament.Location || tournament.Venue,
                  teamId: teamId,
                  teamName: team.name || team.teamName || `Team ${teamId}`,
                  teamCode: team.code || team.teamCode || team.Code || team.TeamCode
                });

                // Found user in this team, no need to check other teams in same tournament
                break;
              } else {
                console.log(`  ✗ User not in team ${team.name || teamId}`);
              }

            } catch (memberErr) {
              console.error(`  Error fetching members for team ${teamId}:`, memberErr);
            }
          }

        } catch (teamErr) {
          console.error(`Error fetching teams for tournament ${tournamentId}:`, teamErr);
        }
      }

      console.log('\n=== FINAL RESULTS ===');
      console.log('User Tournaments Found:', userTournaments.length);
      console.log('Tournament Details:', userTournaments);
      console.log('====================\n');

      setTournaments(userTournaments);

    } catch (err) {
      console.error('Tournament/Team fetch error:', err);
      console.error('Error details:', err.message);
      setTournaments([]);
    }

  } catch (error) {
    console.error('Fetch error:', error);
    setError(error.message);
  } finally {
    setLoading(false);
  }
};

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return 'Invalid Date'; }
  };

  const formatDayDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric'
      });
    } catch { return 'Invalid Date'; }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Error Loading Profile</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const displayUserData = userDetails || userData;
  const userPhotoUrl = getUserPhotoUrl(displayUserData);
  const userPhone = getUserPhone(displayUserData);
  const userState = getUserState(displayUserData);

  return (
    <div className="container mt-5" style={{ marginBottom: '80px' }}>
      <div className="row">
        {/* ID Card */}
        <div className="col-md-3 mb-4">
          <div
            ref={idCardRef}
            className="card shadow h-100"
            style={{
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(109, 169, 197, 0.18)', zIndex: 0 }}></div>

            <div className="card-header text-center border-0" style={{ background: 'rgba(255, 255, 255, 0.14)', position: 'relative', zIndex: 1, color: 'rgba(91,45,139)'}}>
              <h6 className="mb-0"><b>Bishop Games 2025</b></h6>
              <h6 className="mb-0">Vadodara - Anand/Nadiad - Bharuch/Ankleshwar </h6>
            </div>

            <div className="card-body text-center" style={{ position: 'relative', zIndex: 1, color: 'white' }}>
              <div className="mb-3">
                <div className="rounded-circle bg-white d-inline-flex align-items-center justify-content-center overflow-hidden"
                  style={{
                    width: '120px',
                    height: '120px',
                    border: '4px solid white',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    position: 'relative'
                  }}>
                  {userPhotoUrl ? (
                    <>
                      <img
                        src={userPhotoUrl}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <i className="fas fa-user fa-3x text-primary"
                        style={{ position: 'relative', zIndex: 0, opacity: 0 }}></i>
                    </>
                  ) : (
                    <i className="fas fa-user fa-3x text-primary" style={{ position: 'relative', zIndex: 1 }}></i>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <h5 className="fw-bold mb-2">
                  {displayUserData?.firstName && displayUserData?.lastName
                    ? `${displayUserData.firstName} ${displayUserData.lastName}`
                    : displayUserData?.name || 'N/A'}
                </h5>

                {userPhone && (
                  <p className="mb-1">
                    <i className="fas fa-phone me-2"></i>
                    <small className="fw-semibold">{userPhone}</small>
                  </p>
                )}

                {userState && (
                  <p className="mb-1">
                    <i className="fas fa-map-marker-alt me-2"></i>
                    <small className="fw-semibold">{userState}</small>
                  </p>
                )}
              </div>

              <div className="mb-4">
                <div className="bg-white p-3 rounded d-inline-block" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  {qrData ? (
                    <QRCode value={qrData} size={120} level="M" />
                  ) : (
                    <div style={{ width: '120px', height: '120px' }} className="d-flex align-items-center justify-center">
                      <div className="spinner-border text-primary"></div>
                    </div>
                  )}
                </div>
                <p className="mt-2 mb-0"><small className="opacity-75">Scan for details</small></p>
              </div>

              <div className="gap-2" data-exclude-from-capture="true">
                <button className="btn btn-primary fw-bold" onClick={downloadIDCard} disabled={downloading}>
                  {downloading ? (
                    <><div className="spinner-border spinner-border-sm me-2"></div>Downloading...</>
                  ) : (
                    <><i className="fas fa-download me-2"></i>Download ID Card</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>


        {/* Tournament Registrations */}
        <div className="col-md-9 mb-4">
          <div className="card shadow">
            <div className="card-header text-white" style={{ backgroundColor: '#3069B5' }}>
              <h4 className="mb-0"><i className="fas fa-trophy me-2"></i>Tournament Registrations</h4>
            </div>
            <div className="card-body">
              {tournaments.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-info-circle fa-3x mb-3"></i>
                  <h5>No tournaments registered yet</h5>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Tournament Name</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Team Name</th>
                        <th className="text-center">Dashboard</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tournaments.map((t, i) => (
                        <tr key={t.id || i}>
                          <td>{i + 1}</td>
                          <td>
                            <strong>{t.tournamentName}</strong>
                            {t.location && (
                              <small className="d-block text-muted">
                                <i className="fas fa-map-marker-alt me-1"></i>
                                {t.location}
                              </small>
                            )}
                          </td>
                          <td><span className="badge bg-info">{formatDate(t.startDate)}</span></td>
                          <td><span className="badge bg-info">{formatDate(t.endDate)}</span></td>
                          <td>
                            <span className="fw-bold text-primary">{t.teamName}</span>
                            {t.teamCode && (
                              <small className="d-block text-muted">
                                Code: {t.teamCode}
                              </small>
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-primary dashboard-btn"
                             // onClick={() => navigate(`/tournament-dashboard/${t.tournamentId}`)}
                              onClick={() => navigate(`/tournamentdashboard`)}
                              title="View Tournament Dashboard"
                            >
                              <i className="fas fa-chart-line me-1"></i>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Meal Distribution */}
      {/* <div className="row">
        <div className="col-12 mb-4">
          <div className="card shadow">
            <div className="card-header text-white" style={{ backgroundColor: '#3069B5' }}>
              <h4 className="mb-0"><i className="fas fa-utensils me-2"></i>Meal Distribution</h4>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Day</th>
                      <th className="text-center">Breakfast</th>
                      <th className="text-center">Lunch</th>
                      <th className="text-center">Hi-Tea</th>
                      <th className="text-center">Dinner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealDistribution.map((day) => (
                      <tr key={day.id}>
                        <td><strong>{formatDayDate(day.date)}</strong></td>
                        <td className="text-center">
                          {day.breakfast ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          {day.lunch ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          {day.hiTea ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          {day.dinner ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Profile;